import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fileUrl } from "@/lib/upload";
import { PageHeader, Badge, Empty } from "@/components/Kit";
import { ExternalLink } from "lucide-react";

const COLOR = { submitted: "#06B6D4", under_review: "#F59E0B", approved: "#10B981", rejected: "#F43F5E" };

export default function SuperAdmissions() {
  const [status, setStatus] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const { data: schools = [] } = useQuery({ queryKey: ["sa-schools"], queryFn: async () => (await api.get("/superadmin/schools")).data });
  const { data: list = [] } = useQuery({ queryKey: ["sa-admissions"], queryFn: async () => (await api.get("/superadmin/admissions")).data });
  const rows = list.filter((a) => (!status || a.status === status) && (!schoolId || a.school_id === schoolId));
  const counts = list.reduce((m, a) => ({ ...m, [a.status]: (m[a.status] || 0) + 1 }), {});

  return (
    <div data-testid="sa-admissions-page">
      <PageHeader title="Admission Applications" subtitle={`${list.length} applications across all schools · status tracking`} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {[["", "All", list.length], ...Object.keys(COLOR).map((k) => [k, k.replace("_", " "), counts[k] || 0])].map(([k, l, n]) => (
          <button key={k} onClick={() => setStatus(k)} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold capitalize transition ${status === k ? "bg-indigo-600 text-white" : "bg-slate-800/60 text-slate-400 hover:text-white"}`} data-testid={`sa-adm-filter-${k || "all"}`}>{l} <span className="opacity-70">{n}</span></button>
        ))}
        <select className="ep-input ml-auto !w-auto" value={schoolId} onChange={(e) => setSchoolId(e.target.value)} data-testid="sa-adm-school"><option value="">All schools</option>{schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        <a href="/track" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-300 hover:text-white" data-testid="sa-track-link"><ExternalLink size={13} /> Public tracker</a>
      </div>
      <div className="ep-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-4 py-3">Application</th><th className="px-4 py-3">School</th><th className="px-4 py-3">Student</th><th className="px-4 py-3">Class</th><th className="px-4 py-3">Parent</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Last update</th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`sa-adm-row-${a.application_no}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-indigo-300">{a.application_no}</td>
                  <td className="px-4 py-2.5 text-slate-300">{a.school_name}</td>
                  <td className="px-4 py-2.5"><div className="flex items-center gap-2">{a.documents?.photo && <img src={fileUrl(`/api/public/files/${a.documents.photo}`)} alt="" className="h-8 w-8 rounded-lg object-cover" />}<span className="font-medium text-white">{a.student_name}</span></div></td>
                  <td className="px-4 py-2.5"><Badge>{a.class_applying}</Badge></td>
                  <td className="px-4 py-2.5 text-slate-400">{a.parent_name}<br /><span className="text-xs">{a.parent_phone}</span></td>
                  <td className="px-4 py-2.5"><Badge color={COLOR[a.status]}>{a.status.replace("_", " ")}</Badge></td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{new Date((a.timeline || []).slice(-1)[0]?.at || a.created_at).toLocaleString("en-IN")}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={7}><Empty>No applications</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
